/**
 * Modal displaying detailed information about an execution flow node.
 */
import type { FC } from "react";

import { hasValue } from "@arti/utilities";

import type { ExecutionFlowNode } from "../../types";

import {
  extractVisualChanges,
  extractVisualRefinementNotes,
} from "../../utils/executionNodeHelpers";
import { Alert } from "../common/Alert";
import { ContentCard } from "../common/ContentCard";
import { EyeIcon, GearIcon, MenuIcon } from "../common/icons";
import { MetricCard } from "../common/MetricCard";
import { Modal } from "../common/Modal";
import { SectionHeader } from "../common/SectionHeader";
import { StatusBadge } from "../common/StatusBadge";
import { SummaryFormatter } from "../common/SummaryFormatter";
import { Text } from "../common/Text";
import {
  CommitDetailsCard,
  FileChangesTable,
  ReviewFeedbackSection,
} from "./executionNodeModal/index";
import { VisualChangeCard } from "./VisualChangeCard";

const getCommitProviderLabel = (url?: string | null) => {
  if (!url) {
    return "Commit";
  }
  try {
    const host = new URL(url).hostname.toLowerCase();
    if (host.includes("github")) {
      return "GitHub";
    }
    if (host.includes("gitlab")) {
      return "GitLab";
    }
    if (host.includes("bitbucket")) {
      return "Bitbucket";
    }
    return host;
  } catch {
    return "Commit";
  }
};

interface ExecutionNodeModalProps {
  node: ExecutionFlowNode;
  onClose: () => void;
  branchName?: string | null;
}

export const ExecutionNodeModal: FC<ExecutionNodeModalProps> = ({
  node,
  onClose,
  branchName,
}) => {
  const totalAdded = node.file_changes.reduce(
    (acc, f) => acc + (f.added_lines ?? 0),
    0,
  );
  const totalRemoved = node.file_changes.reduce(
    (acc, f) => acc + (f.removed_lines ?? 0),
    0,
  );
  const hasCommitDetails =
    hasValue(node.commitSha) ||
    hasValue(node.commitUrl) ||
    hasValue(node.commitMessage) ||
    hasValue(branchName);
  const commitProviderLabel = getCommitProviderLabel(node.commitUrl);

  const visualChanges = extractVisualChanges(node);
  const visualRefinementNotes = extractVisualRefinementNotes(node);

  return (
    <Modal
      title="Execution Node Details"
      subtitle={node.plan_item_id}
      icon={<GearIcon size={16} />}
      onClose={onClose}
      widthClass="max-w-4xl"
    >
      <div className="space-y-4">
        {/* Header Stats */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-5">
          <MetricCard
            label="Status"
            value={<StatusBadge status={node.status} size="sm" />}
          />
          {node.assigned_node && (
            <MetricCard
              label="Worker Node"
              value={
                <Text
                  variant="mono"
                  className="block max-w-[120px] truncate"
                  title={node.assigned_node}
                >
                  {node.assigned_node}
                </Text>
              }
            />
          )}
          <MetricCard label="Files Changed" value={node.file_changes.length} />
          {visualChanges.length > 0 && (
            <MetricCard
              label="Visual Changes"
              value={visualChanges.length}
              variant="primary"
            />
          )}
          <MetricCard
            label="Lines Added"
            value={`+${totalAdded}`}
            variant="success"
          />
          <MetricCard
            label="Lines Removed"
            value={`-${totalRemoved}`}
            variant="danger"
          />
        </div>

        {/* Commit Details */}
        {hasCommitDetails && (
          <CommitDetailsCard
            commitSha={node.commitSha}
            commitUrl={node.commitUrl}
            commitMessage={node.commitMessage}
            branchName={branchName}
            commitProviderLabel={commitProviderLabel}
          />
        )}

        {/* Summary */}
        {node.summary && (
          <div>
            <SectionHeader title="Summary" icon={<MenuIcon size={16} />} />
            <ContentCard className="overflow-x-auto text-sm leading-relaxed text-gray-700 dark:text-gray-300">
              <SummaryFormatter text={node.summary} />
            </ContentCard>
          </div>
        )}

        {/* Visual Refinement Notes */}
        {visualRefinementNotes.length > 0 && (
          <div>
            <SectionHeader
              title="Visual Refinement"
              icon={<EyeIcon size={16} />}
            />
            <Alert
              variant="info"
              className="space-y-3 !border-slate-300 !bg-slate-100/80 dark:!border-emerald-500/20 dark:!bg-emerald-500/10"
            >
              {visualRefinementNotes.map((note, idx) => (
                <div
                  key={idx}
                  className="text-sm leading-relaxed text-slate-800 dark:text-slate-100"
                >
                  <SummaryFormatter text={note} />
                </div>
              ))}
            </Alert>
          </div>
        )}

        {/* Visual Changes */}
        {visualChanges.length > 0 && (
          <div>
            <SectionHeader
              title="UI/UX Visual Changes"
              icon={<EyeIcon size={16} />}
            />
            <div className="space-y-3">
              {visualChanges.map(({ file, change }, idx) => (
                <VisualChangeCard
                  key={`${file.path}-${idx}`}
                  file={file}
                  change={change}
                />
              ))}
            </div>
          </div>
        )}

        {/* Review Feedback */}
        {node.review_feedback && node.review_feedback.length > 0 && (
          <ReviewFeedbackSection feedback={node.review_feedback} />
        )}

        {/* File Changes Table */}
        {node.file_changes.length > 0 && (
          <FileChangesTable fileChanges={node.file_changes} />
        )}

        {/* Metadata / Debug Info */}
        <div className="grid grid-cols-2 gap-3 border-t border-gray-100 pt-4 dark:border-white/5">
          <MetricCard
            label="Repository"
            value={<Text variant="mono">{node.repository_slug}</Text>}
          />
          <MetricCard
            label="IDs"
            value={
              <div className="space-y-0.5 text-xs font-normal">
                <div className="truncate" title={`Ticket: ${node.ticket_id}`}>
                  Ticket: {node.ticket_id}
                </div>
                <div className="truncate" title={`Request: ${node.request_id}`}>
                  Request: {node.request_id.substring(0, 8)}...
                </div>
              </div>
            }
          />
        </div>

        {node.error && (
          <Alert variant="error" title="Error Details">
            <pre className="mt-1 overflow-x-auto whitespace-pre-wrap font-mono text-xs">
              {node.error}
            </pre>
          </Alert>
        )}
      </div>
    </Modal>
  );
};
